import { Component, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { IExam } from 'src/app/interface/dto';
import { examDefault } from 'src/app/interface/dtoDefaults';
import { ExamService } from 'src/app/services/exam-service.service';
import { SnackbarService } from 'src/app/services/snackbar.service';

@Component({
  selector: 'app-psychometric-report',
  templateUrl: './psychometric-report.component.html',
  styleUrls: ['./psychometric-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PsychometricReportComponent implements OnInit {

  exam: IExam = examDefault;
  isGenerating = false;
  html: string = '';
  showReport = false;
  noReportFound = false;

  psychometricReport: {
    examinationID: number;
    candidateIDs: string;
    skipImportCandidateData: boolean;
    hideConsistencyChecks: boolean;
    consistencyThresholdPercentage: number;
    examinationScheduleID: number | null;
  } = {
    examinationID: 0,
    candidateIDs: '',
    skipImportCandidateData: false,
    hideConsistencyChecks: true,
    consistencyThresholdPercentage: 70,
    examinationScheduleID: null
  };


  constructor(
    private examService: ExamService,
    private snackBar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private serializer: UrlSerializer,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
   // Get exam from route params
   this.route.paramMap.subscribe(params => {
     const examIdStr = params.get('examid');
     const examId = examIdStr ? parseInt(examIdStr, 10) : null;

     const examSchIdStr = params.get('examschid');
     const examScheduleId = examSchIdStr ? parseInt(examSchIdStr, 10) : null;

     // Set schedule ID if available
     this.psychometricReport.examinationScheduleID = examScheduleId;
 
     if (examId) {
       // Try to find the exam in selectedExams
       const matchedExam = this.examService.selectedExams.find(e => e.id === examId);
       this.exam = matchedExam ?? { ...examDefault, id: examId };
 
       // Set report exam ID
       this.psychometricReport.examinationID = examId;
     } else {
       // No examId in route
       this.snackBar.error('No examination selected. Redirecting to examinations page...');
       setTimeout(() => this.router.navigate(['/exams']), 2000);
     }
   });
 }

  async fetchReport() {
    if (this.isGenerating) return;
    
    // Reset states
    this.isGenerating = true;
    this.html = '';
    this.showReport = false;
    this.noReportFound = false;

    // Set examination ID
    if (this.exam && this.exam.id) {
      this.psychometricReport.examinationID = this.exam.id;
    }

    if (!this.psychometricReport.examinationID || this.psychometricReport.examinationID <= 0) {
      this.snackBar.error('No examination selected. Redirecting to examinations page...');
      setTimeout(() => {
        this.router.navigate(['/exams']);
      }, 2000);
      return;
    }

    try {
      const reportTree = this.router.createUrlTree([], { queryParams: this.psychometricReport });
      
      const params = this.serializer.serialize(reportTree).split('?', 2)[1];

      const response = await this.examService.getPsychometricTestReport(params) as { file: string; fileName: string };

      // Check if reportHTML is returned and not null/empty
      if (response?.file) {
        const binary = atob(response.file);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        this.html = new TextDecoder().decode(bytes);
        this.showReport = true;
        this.noReportFound = false;
        this.snackBar.success('Psychometric Report Generated Successfully');
        
        // Scroll to report section
        setTimeout(() => {
          document.getElementById('reportSection')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        this.showReport = false;
        this.noReportFound = true;
        this.snackBar.error('No report data available');
      }
      
    } catch (err) {
      this.showReport = false;
      this.noReportFound = true;

      let errorMessage = 'Something went wrong';
      try {
        const cleanMessage = err.message.split('#')[0];
        const parsed = JSON.parse(cleanMessage);
        errorMessage = parsed?.message ?? errorMessage;
      } catch {
        errorMessage = err.message; // fallback if it's plain text
      }
      
      this.snackBar.error(errorMessage);
    } finally {
      this.isGenerating = false;
    }
  }

  printReport() {
    if (!this.psychometricReport.hideConsistencyChecks) {
      this.toggleConsistency();
    }
    window.print();
  }

  toggleConsistency() {
    const containers = document.querySelectorAll('#questionscontainer');
    
    if (!containers.length) return;
    
    containers.forEach(container => {
      const detailsList = container.querySelectorAll(
        'details.consistency-details') as NodeListOf<HTMLDetailsElement>;
      
      detailsList.forEach(details => {
        if (details.hasAttribute('open')) {
          this.renderer.removeAttribute(details, 'open');
        } else {
          this.renderer.setAttribute(details, 'open', '');
        }
      });
    });
  }
}